import * as React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Receipt,
  Settings as SettingsIcon,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { extraNav, type ExtraNavItem } from '@/pages/extra/routes';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/menu';
import { useAuth } from '@/auth/AuthProvider';
import { AppFooter } from '@/app/AppFooter';
import { cn } from '@/lib/utils';

/**
 * The authenticated application frame.
 *
 * A fixed sidebar on desktop, a slide-in drawer on mobile, and a top bar carrying the
 * user menu (the environment is shown in the footer). The sidebar's own groups come first; the banking /
 * books / cloud pages contribute theirs through `extraNav`, grouped by `item.group`,
 * so the two page families stay independently built.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Match the route exactly (used for the dashboard "/"). */
  end?: boolean;
}

const SALES_NAV: NavItem[] = [
  { to: '/invoices', label: 'Invoices', icon: Receipt },
  { to: '/clients', label: 'Clients', icon: Users },
  { to: '/recurring', label: 'Recurring', icon: CalendarClock },
  { to: '/payments', label: 'Payments', icon: CreditCard },
];

/** Groups the extra nav items in the fixed section order the sidebar renders. */
const EXTRA_GROUP_ORDER: ExtraNavItem['group'][] = ['Banking', 'Books', 'Cloud'];

function groupExtraNav(): Array<{ group: ExtraNavItem['group']; items: ExtraNavItem[] }> {
  return EXTRA_GROUP_ORDER.map((group) => ({
    group,
    items: extraNav.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const location = useLocation();

  // Close the mobile drawer on navigation; leaving it open over the new page is a
  // small but constant irritation on a phone.
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    // Column: the sidebar + content row on top, then the footer across the whole
    // viewport width (under the sidebar too).
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-muted/20">
      <div className="flex min-h-0 flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden w-60 shrink-0 flex-col border-r bg-background lg:flex">
          <SidebarContent />
        </aside>

        {/* Mobile drawer */}
        {drawerOpen ? (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <aside className="absolute left-0 top-0 flex h-full w-64 flex-col border-r bg-background shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <Brand />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close menu"
                >
                  <X />
                </Button>
              </div>
              <SidebarContent hideBrand />
            </aside>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenDrawer={() => setDrawerOpen(true)} />
          {/* `relative` contains absolutely positioned descendants (e.g. `sr-only` hints) inside
            the scroll pane; without it they are placed against the page, making the document
            taller than the viewport so the whole shell, nav included, scrolls away.
            `overscroll-contain` stops scroll chaining to the document at the ends. */}
          <main className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</main>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2 font-semibold">
      <Wallet className="size-5 text-primary" aria-hidden="true" />
      <span className="text-sm">Albuelis</span>
    </Link>
  );
}

function SidebarContent({ hideBrand = false }: { hideBrand?: boolean }) {
  const extra = groupExtraNav();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {hideBrand ? null : (
        <div className="flex h-12 items-center border-b px-4">
          <Brand />
        </div>
      )}
      <nav className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain p-3" aria-label="Primary">
        <NavSection>
          <NavRow to="/" label="Overview" icon={LayoutDashboard} end />
        </NavSection>

        <NavSection title="Sales">
          {SALES_NAV.map((item) => (
            <NavRow key={item.to} to={item.to} label={item.label} icon={item.icon} />
          ))}
        </NavSection>

        {extra.map((section) => (
          <NavSection key={section.group} title={section.group}>
            {section.items.map((item) => (
              <NavRow key={item.to} to={item.to} label={item.label} icon={item.icon} end={item.end} />
            ))}
          </NavSection>
        ))}
      </nav>
    </div>
  );
}

function NavSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      {title ? (
        <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </p>
      ) : null}
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function NavRow({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isActive
              ? 'bg-primary/10 font-medium text-primary'
              : 'text-foreground/80 hover:bg-accent hover:text-foreground',
          )
        }
      >
        <Icon className="size-4 shrink-0" />
        <span className="truncate">{label}</span>
      </NavLink>
    </li>
  );
}

function TopBar({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const auth = useAuth();
  const navigate = useNavigate();

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b bg-background px-3 sm:px-4">
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        onClick={onOpenDrawer}
        aria-label="Open menu"
      >
        <Menu />
      </Button>

      <div className="lg:hidden">
        <Brand />
      </div>

      <div className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              aria-label={`Account menu for ${auth.profile?.email ?? auth.profile?.name ?? 'you'}`}
            >
              <span className="grid size-6 place-items-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                {initials(auth.profile?.name ?? auth.profile?.email ?? '?')}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              {auth.profile?.email ?? auth.profile?.name ?? 'Signed in'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <SettingsIcon />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void auth.signOut()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function initials(nameOrEmail: string): string {
  const base = nameOrEmail.split('@')[0] ?? nameOrEmail;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
